// ============================================================
// Trends — cash flow, monthly income/expense, savings, debt payoff
// ============================================================
// Four sections:
//   1) Checking cash flow — balance history + projected future (NEW)
//   2) Monthly income vs expenses — grouped bar chart (NEW)
//   3) Savings accumulation — goal trajectory lines
//   4) Debt payoff — per-debt or stacked
//
// Uses the shared loadFullHistory / allTransactions cache from store.js
// so it doesn't re-fetch if Dashboard already loaded it.
// ============================================================

import { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  LineChart,
  BarChart,
  Line,
  Bar,
  Area,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import { api } from '../api.js';
import { fmtCents, fmtDateShort, todayIso, addDaysIso } from '../format.js';

const HORIZONS = [
  { value: 90,   label: 'Next 3 months' },
  { value: 183,  label: 'Next 6 months' },
  { value: 365,  label: 'Next 1 year' },
  { value: 548,  label: 'Next 18 months' },
  { value: 730,  label: 'Next 2 years' },
  { value: 1825, label: 'Next 5 years' },
];

const SERIES_COLORS = [
  'var(--teal)',
  'var(--amber)',
  'var(--purple)',
  '#be185d',
  'var(--blue)',
  'var(--green)',
  '#9f1239',
  '#0891b2',
];

// ─── Small stat strip rendered above each chart ───────────────────────────

function StatStrip({ stats }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 0,
        borderBottom: '1px solid var(--border2)',
        marginBottom: 12,
        paddingBottom: 12,
        flexWrap: 'wrap',
        rowGap: 8,
      }}
    >
      {stats.map((s, i) => (
        <div
          key={i}
          style={{
            flex: '1 1 0',
            minWidth: 80,
            paddingRight: 12,
            borderRight: i < stats.length - 1 ? '1px solid var(--border2)' : 'none',
            paddingLeft: i > 0 ? 12 : 0,
          }}
        >
          <div className="muted tiny" style={{ marginBottom: 2 }}>{s.label}</div>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontWeight: 600,
              fontSize: 14,
              color: s.color ?? 'var(--text)',
            }}
          >
            {s.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Shared tooltip ────────────────────────────────────────────────────────

function MoneyTooltip({ active, payload, label, showTotal }) {
  if (!active || !payload || payload.length === 0) return null;
  const validPayload = payload.filter(p => p.value != null && p.value !== 0);
  if (validPayload.length === 0) return null;
  const total = validPayload.reduce((sum, p) => sum + (p.value || 0), 0);
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r1)',
        padding: '8px 10px',
        fontSize: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        fontFamily: 'var(--font-sans)',
        minWidth: 160,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{fmtDateShort(label)}</div>
      {validPayload.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ color: p.color }}>● {p.name}</span>
          <span style={{ fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-mono)' }}>
            {fmtCents(Math.round(p.value * 100))}
          </span>
        </div>
      ))}
      {showTotal && validPayload.length > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            marginTop: 4,
            paddingTop: 4,
            borderTop: '1px solid var(--border2)',
            fontWeight: 600,
          }}
        >
          <span>Total</span>
          <span style={{ fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-mono)' }}>
            {fmtCents(Math.round(total * 100))}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Bar tooltip for monthly view ─────────────────────────────────────────

function MonthlyTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const income  = payload.find(p => p.dataKey === 'income');
  const expense = payload.find(p => p.dataKey === 'expense');
  const savings = payload.find(p => p.dataKey === 'savings');
  const netLiquid = ((income?.value ?? 0) - (expense?.value ?? 0)) * 100;
  const netTrue   = netLiquid - (savings?.value ?? 0) * 100;
  const row = (label, val, color) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
      <span style={{ color }}>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-mono)' }}>
        {fmtCents(Math.round(val * 100))}
      </span>
    </div>
  );
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--r1)', padding: '8px 10px', fontSize: 12,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)', minWidth: 170,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {income  && row('● Income',   income.value,  'var(--green)')}
      {expense && row('● Expenses', expense.value, 'var(--red)')}
      {savings && savings.value > 0 && row('● Savings', savings.value, 'var(--blue)')}
      <div style={{ borderTop: '1px solid var(--border2)', marginTop: 4, paddingTop: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontWeight: 600, color: netLiquid >= 0 ? 'var(--green)' : 'var(--red)' }}>
          <span>Net liquid</span>
          <span style={{ fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-mono)' }}>{fmtCents(netLiquid)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontWeight: 600, color: netTrue >= 0 ? 'var(--green)' : 'var(--red)', opacity: 0.8 }}>
          <span>Net true</span>
          <span style={{ fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-mono)' }}>{fmtCents(netTrue)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Cash flow row (income / expense / net per column) ───────────────────────

function CashFlowRow({ label, color, cols, field, bold, netColor, dim }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '72px 1fr 1fr 1fr', gap: 0, marginBottom: 5, alignItems: 'center' }}>
      <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: bold ? 600 : 400, paddingRight: 4 }}>
        {label}
      </div>
      {cols.map((col) => {
        const cents = Math.round((col[field] ?? 0) * 100);
        const netCol = netColor && cents !== 0;
        const c = netCol
          ? cents >= 0 ? 'var(--green)' : 'var(--red)'
          : color;
        return (
          <div
            key={col.key}
            style={{
              textAlign: 'right',
              fontFamily: 'var(--font-mono)',
              fontWeight: bold ? 700 : 500,
              fontSize: 13,
              color: c ?? 'var(--text)',
              opacity: dim || (col.isProjected && !netColor) ? 0.75 : 1,
              paddingRight: 4,
            }}
          >
            {cents === 0 ? <span style={{ color: 'var(--text3)' }}>—</span> : fmtCents(Math.abs(cents))}
            {col.isProjected && cents !== 0 && (
              <span style={{ fontSize: 9, color: 'var(--blue)', marginLeft: 2 }}>est</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main screen
// ─────────────────────────────────────────────────────────────────────────────

export default function Trends({ state, lookups, loadFullHistory, showToast }) {
  const [horizonDays, setHorizonDays] = useState(365);
  const [debtMode, setDebtMode] = useState('lines');
  const [loadError, setLoadError] = useState(null);

  // Use the shared allTransactions cache from store. Trigger load if not ready.
  useEffect(() => {
    if (state.allTransactions === null && !state.allTransactionsLoading && loadFullHistory) {
      loadFullHistory().catch(e => setLoadError(e.message));
    }
  }, [state.allTransactions, state.allTransactionsLoading, loadFullHistory]);

  const allTxns = state.allTransactions; // null = loading

  const today = todayIso();
  const endIso = useMemo(() => addDaysIso(today, horizonDays), [today, horizonDays]);

  const startIso = useMemo(() => {
    if (!allTxns || allTxns.length === 0) return addDaysIso(today, -90);
    return allTxns.reduce(
      (min, t) => t.occurred_on < min ? t.occurred_on : min,
      allTxns[0].occurred_on,
    );
  }, [allTxns, today]);

  // ── Checking account data ──────────────────────────────────────────────
  const checkingId = useMemo(
    () => state.accounts.find(a => a.name === 'Checking')?.id,
    [state.accounts],
  );

  const checkingCurrentBalance = useMemo(() => {
    if (!allTxns || !checkingId) return 0;
    return allTxns
      .filter(t => t.account_id === checkingId)
      .reduce((sum, t) => sum + t.amount_cents, 0);
  }, [allTxns, checkingId]);

  // Fetch the forecast horizon from the API — same source of truth as the Forecast screen.
  // This replaces the old client-side expandRule projection which ignored paid/skipped items,
  // forecast_overrides (adjusted amounts, WF CC Club Tab override, rescheduled dates), etc.
  const [forecastRows, setForecastRows] = useState(null);
  const [forecastEndIso, setForecastEndIso] = useState(null);

  useEffect(() => {
    const from = addDaysIso(today, 1);
    const to = endIso;
    setForecastRows(null);
    setForecastEndIso(to);
    api.getForecast(from, to)
      .then(rows => setForecastRows(rows))
      .catch(() => setForecastRows([]));
  }, [today, endIso]);

  const checkingChartData = useMemo(() => {
    if (!allTxns || !checkingId) return [];
    return buildCheckingBalanceSeries(
      checkingId, checkingCurrentBalance, allTxns,
      forecastRows, state.accounts, startIso, endIso, today,
    );
  }, [allTxns, checkingId, checkingCurrentBalance, forecastRows, state.accounts, startIso, endIso, today]);

  const checkingYDomain = useMemo(() => {
    if (!checkingChartData.length) return ['auto', 'auto'];
    const vals = checkingChartData
      .flatMap(p => [p.balance, p.projected])
      .filter(v => v != null);
    if (!vals.length) return ['auto', 'auto'];
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const pad = Math.max((hi - lo) * 0.12, 500); // at least $500 padding
    // Floor at 0 only if the balance actually goes negative; otherwise
    // anchor tightly to the data so the sawtooth pattern is visible.
    const floor = lo - pad < 0 ? Math.min(0, lo - pad) : lo - pad;
    return [Math.floor(floor / 100) * 100, Math.ceil((hi + pad) / 100) * 100];
  }, [checkingChartData]);

  const checkingProjectedLow = useMemo(() => {
    const futurePoints = checkingChartData.filter(p => p.date > today && p.projected != null);
    if (futurePoints.length === 0) return null;
    return Math.min(...futurePoints.map(p => p.projected));
  }, [checkingChartData, today]);

  const checkingProjectedHigh = useMemo(() => {
    const futurePoints = checkingChartData.filter(p => p.date > today && p.projected != null);
    if (futurePoints.length === 0) return null;
    return Math.max(...futurePoints.map(p => p.projected));
  }, [checkingChartData, today]);

  // ── Monthly cash flow: last month / this month actuals + next month from forecast API ──
  const [nextMonthForecast, setNextMonthForecast] = useState(null);

  useEffect(() => {
    // Always fetch next month fresh from the forecast engine — same source as the Forecast screen.
    // This avoids drift from our own expandRule logic (different discretionary counts, WF CC overrides, etc.)
    const [ty, tm] = today.split('-').map(Number);
    const nextMonthDate = new Date(Date.UTC(ty, tm, 1));
    const ny = nextMonthDate.getUTCFullYear();
    const nm = nextMonthDate.getUTCMonth() + 1;
    const from = `${ny}-${String(nm).padStart(2, '0')}-01`;
    const lastDay = new Date(Date.UTC(ny, nm, 0)).getUTCDate();
    const to = `${ny}-${String(nm).padStart(2, '0')}-${lastDay}`;
    api.getForecast(from, to)
      .then(rows => setNextMonthForecast({ rows, monthStr: from.slice(0, 7) }))
      .catch(() => setNextMonthForecast(null));
  }, [today]);

  const cashFlowData = useMemo(() => {
    if (!allTxns || !checkingId) return null;
    return buildCashFlowSummary(allTxns, checkingId, today, lookups, nextMonthForecast);
  }, [allTxns, checkingId, today, lookups, nextMonthForecast]);

  // ── Savings / Debt ─────────────────────────────────────────────────────
  const savingsSeries = useMemo(() => {
    if (!allTxns) return [];
    return buildSavingsSeries(state, allTxns, startIso, endIso, today);
  }, [state, allTxns, startIso, endIso, today]);

  const debtSeries = useMemo(() => {
    if (!allTxns) return [];
    return buildDebtSeries(state, allTxns, startIso, endIso, today);
  }, [state, allTxns, startIso, endIso, today]);

  const savingsChartData = useMemo(() => zipSeries(savingsSeries), [savingsSeries]);
  const debtChartData    = useMemo(() => zipSeries(debtSeries), [debtSeries]);

  const goalTargets = useMemo(() => {
    return state.goals
      .filter(g => !g.archived && g.account_id && g.target_cents > 0)
      .map((g, i) => ({
        name: g.name,
        target: g.target_cents / 100,
        color: SERIES_COLORS[i % SERIES_COLORS.length],
      }));
  }, [state.goals]);

  // ── Loading / error ────────────────────────────────────────────────────
  if (loadError) {
    return (
      <div className="screen">
        <h1>Trends</h1>
        <div className="card">
          <div className="muted">Couldn't load transaction history: {loadError}</div>
        </div>
      </div>
    );
  }

  const isLoading = allTxns === null;

  return (
    <div className="screen">
      <div className="row between" style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Trends</h1>
        <select
          value={horizonDays}
          onChange={e => setHorizonDays(parseInt(e.target.value, 10))}
          style={{ flex: '0 0 auto', maxWidth: 160 }}
          disabled={isLoading}
        >
          {HORIZONS.map(h => (
            <option key={h.value} value={h.value}>{h.label}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="full-spinner" style={{ padding: '40px 0' }}>
          <div className="spinner" />
          <div className="muted tiny" style={{ marginTop: 8 }}>Loading history…</div>
        </div>
      ) : (
        <>
          {/* ─── 1. Checking Balance ─── */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="row between" style={{ marginBottom: 8, alignItems: 'baseline' }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Checking balance</h2>
              <span className="muted tiny">history + projected</span>
            </div>

            <StatStrip stats={[
              {
                label: 'Current',
                value: fmtCents(checkingCurrentBalance),
                color: checkingCurrentBalance >= 0 ? 'var(--text)' : 'var(--red)',
              },
              {
                label: 'Projected low',
                value: checkingProjectedLow != null
                  ? fmtCents(Math.round(checkingProjectedLow * 100))
                  : '—',
                color: checkingProjectedLow != null && checkingProjectedLow < 10
                  ? 'var(--red)'
                  : 'var(--text)',
              },
              {
                label: 'Projected high',
                value: checkingProjectedHigh != null
                  ? fmtCents(Math.round(checkingProjectedHigh * 100))
                  : '—',
                color: 'var(--green)',
              },
            ]} />

            {checkingChartData.length === 0 ? (
              <div className="empty">
                <div className="icon">∅</div>
                <div className="muted tiny">No checking transactions found.</div>
              </div>
            ) : (
              <>
                <div style={{ width: '100%', height: 240 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={checkingChartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="checkingFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--teal)" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="var(--teal)" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="projFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--blue)" stopOpacity={0.10} />
                          <stop offset="95%" stopColor="var(--blue)" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border2)" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={fmtTickDate}
                        minTickGap={50}
                        stroke="var(--text3)"
                        fontSize={11}
                      />
                      <YAxis
                        tickFormatter={fmtTickMoney}
                        stroke="var(--text3)"
                        fontSize={11}
                        width={52}
                        domain={checkingYDomain}
                      />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const hist = payload.find(p => p.dataKey === 'balance');
                          const proj = payload.find(p => p.dataKey === 'projected');
                          const val = hist?.value ?? proj?.value;
                          const isProj = hist?.value == null;
                          if (val == null) return null;
                          return (
                            <div style={{
                              background: 'var(--surface)', border: '1px solid var(--border)',
                              borderRadius: 'var(--r1)', padding: '8px 10px', fontSize: 12,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.08)', minWidth: 140,
                            }}>
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>{fmtDateShort(label)}</div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                <span style={{ color: isProj ? 'var(--blue)' : 'var(--teal)' }}>
                                  ● {isProj ? 'Projected' : 'Balance'}
                                </span>
                                <span style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                                  {fmtCents(Math.round(val * 100))}
                                </span>
                              </div>
                              {isProj && (
                                <div className="muted tiny" style={{ marginTop: 4 }}>estimated</div>
                              )}
                            </div>
                          );
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="balance"
                        name="Balance"
                        stroke="var(--teal)"
                        strokeWidth={2}
                        fill="url(#checkingFill)"
                        dot={false}
                        isAnimationActive={false}
                        connectNulls={false}
                      />
                      <Area
                        type="monotone"
                        dataKey="projected"
                        name="Projected"
                        stroke="var(--blue)"
                        strokeWidth={1.5}
                        strokeDasharray="5 3"
                        fill="url(#projFill)"
                        dot={false}
                        isAnimationActive={false}
                        connectNulls={false}
                      />
                      <ReferenceLine
                        x={today}
                        stroke="var(--text2)"
                        strokeDasharray="4 4"
                        label={{ value: 'today', position: 'top', fill: 'var(--text2)', fontSize: 10 }}
                      />
                      <ReferenceLine
                        y={0}
                        stroke="var(--red)"
                        strokeOpacity={0.4}
                        strokeWidth={1}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <div className="muted tiny" style={{ marginTop: 6 }}>
                  Projection uses your active recurring rules. Transfers in/out and debt payments
                  are included as Checking debits.
                </div>
              </>
            )}
          </div>

          {/* ─── 2. Monthly cash flow ─── */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="row between" style={{ marginBottom: 12, alignItems: 'baseline' }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Monthly cash flow</h2>
              <span className="muted tiny">excl. transfers</span>
            </div>

            {!cashFlowData ? (
              <div className="empty">
                <div className="icon">∅</div>
                <div className="muted tiny">Not enough history yet.</div>
              </div>
            ) : (
              <>
                {/* Column headers — label col + 3 months */}
                <div style={{ display: 'grid', gridTemplateColumns: '72px 1fr 1fr 1fr', gap: 0, marginBottom: 10 }}>
                  <div />
                  {cashFlowData.map((col, i) => (
                    <div
                      key={col.key}
                      style={{
                        textAlign: 'right',
                        paddingBottom: 6,
                        paddingRight: 4,
                        borderBottom: `2px solid ${i === 2 ? 'var(--blue)' : 'var(--border2)'}`,
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 700, color: i === 2 ? 'var(--blue)' : 'var(--text2)' }}>
                        {col.label}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>{col.sublabel}</div>
                    </div>
                  ))}
                </div>

                {/* Income */}
                <CashFlowRow label="Income" color="var(--green)" cols={cashFlowData} field="income" />

                {/* Expenses (incl. debt payments) */}
                <CashFlowRow label="Expenses" color="var(--red)" cols={cashFlowData} field="expense" />

                {/* Savings — blue, separate section */}
                <div style={{ marginTop: 4, marginBottom: 4 }}>
                  <CashFlowRow label="→ Savings" color="var(--blue)" cols={cashFlowData} field="savings" dim />
                </div>

                {/* Divider + two net rows */}
                <div style={{ borderTop: '1px solid var(--border2)', marginTop: 6, paddingTop: 8 }}>
                  <CashFlowRow label="Net liquid" color={null} cols={cashFlowData} field="netLiquid" bold netColor />
                  <div style={{ marginTop: 3 }}>
                    <CashFlowRow label="Net true" color={null} cols={cashFlowData} field="net" bold netColor />
                  </div>
                </div>

                {/* Legend for the two nets */}
                <div style={{ display: 'flex', gap: 12, marginTop: 4, marginBottom: 2, flexWrap: 'wrap' }}>
                  <span className="muted tiny"><strong>Net liquid</strong> = income − expenses (savings still in your pocket)</span>
                  <span className="muted tiny"><strong>Net true</strong> = income − expenses − savings</span>
                </div>

                {/* Bar chart */}
                <div style={{ width: '100%', height: 200, marginTop: 16 }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={cashFlowData}
                      margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
                      barCategoryGap="28%"
                      barGap={3}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border2)" vertical={false} />
                      <XAxis dataKey="label" stroke="var(--text3)" fontSize={11} tick={{ fill: 'var(--text3)' }} />
                      <YAxis tickFormatter={fmtTickMoney} stroke="var(--text3)" fontSize={11} width={52} />
                      <Tooltip content={<MonthlyTooltip />} />
                      <Legend
                        wrapperStyle={{ fontSize: 12, paddingTop: 4 }}
                        formatter={(v) => v === 'income' ? 'Income' : v === 'expense' ? 'Expenses' : 'Savings'}
                      />
                      <Bar dataKey="income"  name="income"  fill="var(--green)" fillOpacity={0.75} radius={[3,3,0,0]} isAnimationActive={false} />
                      <Bar dataKey="expense" name="expense" fill="var(--red)"   fillOpacity={0.65} radius={[3,3,0,0]} isAnimationActive={false} />
                      <Bar dataKey="savings" name="savings" fill="var(--blue)"  fillOpacity={0.55} radius={[3,3,0,0]} isAnimationActive={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="muted tiny" style={{ marginTop: 6 }}>
                  Paychecks arriving late in the month (e.g. May 30) are attributed to the 1st of the
                  following month. Debt payments included in Expenses. Next month = recurring rules only.
                </div>
              </>
            )}
          </div>

          {/* ─── 3. Savings accumulation ─── */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="row between" style={{ marginBottom: 8, alignItems: 'baseline' }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Savings accumulation</h2>
              <span className="muted tiny">
                {savingsSeries.length === 0
                  ? 'No linked goals'
                  : `${savingsSeries.length} ${savingsSeries.length === 1 ? 'goal' : 'goals'}`}
              </span>
            </div>

            {savingsSeries.length === 0 ? (
              <div className="empty">
                <div className="icon">∅</div>
                <div>No goals are linked to a savings account.</div>
                <div className="muted tiny" style={{ marginTop: 4 }}>
                  Link a goal in Manage → Goals to see its trajectory here.
                </div>
              </div>
            ) : (
              <>
                <StatStrip stats={savingsSeries.map((s, i) => ({
                  label: s.label,
                  value: fmtCents(Math.round((s.points[s.points.length - 1]?.value ?? 0) * 100)),
                  color: SERIES_COLORS[i % SERIES_COLORS.length],
                }))} />
                <div style={{ width: '100%', height: 240 }}>
                  <ResponsiveContainer>
                    <LineChart data={savingsChartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border2)" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={fmtTickDate}
                        minTickGap={40}
                        stroke="var(--text3)"
                        fontSize={11}
                      />
                      <YAxis
                        tickFormatter={fmtTickMoney}
                        stroke="var(--text3)"
                        fontSize={11}
                        width={52}
                      />
                      <Tooltip content={<MoneyTooltip showTotal />} />
                      <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
                      <ReferenceLine
                        x={today}
                        stroke="var(--text2)"
                        strokeDasharray="4 4"
                        label={{ value: 'today', position: 'top', fill: 'var(--text2)', fontSize: 10 }}
                      />
                      {goalTargets.map(g => (
                        <ReferenceLine
                          key={`target-${g.name}`}
                          y={g.target}
                          stroke={g.color}
                          strokeDasharray="2 4"
                          strokeOpacity={0.45}
                        />
                      ))}
                      {savingsSeries.map((s, i) => (
                        <Line
                          key={s.key}
                          type="monotone"
                          dataKey={s.key}
                          name={s.label}
                          stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                          strokeWidth={2}
                          dot={false}
                          isAnimationActive={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </div>

          {/* ─── 4. Debt payoff ─── */}
          <div className="card">
            <div className="row between" style={{ marginBottom: 8, alignItems: 'baseline' }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Debt payoff</h2>
              <div className="row" style={{ gap: 4 }}>
                <button
                  className={debtMode === 'lines' ? 'primary' : ''}
                  onClick={() => setDebtMode('lines')}
                  style={{ padding: '4px 10px', fontSize: 12 }}
                >
                  By debt
                </button>
                <button
                  className={debtMode === 'stacked' ? 'primary' : ''}
                  onClick={() => setDebtMode('stacked')}
                  style={{ padding: '4px 10px', fontSize: 12 }}
                >
                  Total
                </button>
              </div>
            </div>

            {debtSeries.length === 0 ? (
              <div className="empty">
                <div className="icon">∅</div>
                <div>No debts are linked to an account.</div>
              </div>
            ) : (
              <>
                <StatStrip stats={[
                  {
                    label: 'Total debt now',
                    value: fmtCents(
                      Math.round(debtSeries.reduce((sum, s) => {
                        const pt = s.points.find(p => p.date === today);
                        return sum + (pt?.value ?? 0) * 100;
                      }, 0)),
                    ),
                    color: 'var(--red)',
                  },
                  {
                    label: `Est. at ${fmtTickDate(addDaysIso(today, horizonDays))}`,
                    value: fmtCents(
                      Math.round(debtSeries.reduce((sum, s) => {
                        const pt = s.points[s.points.length - 1];
                        return sum + (pt?.value ?? 0) * 100;
                      }, 0)),
                    ),
                    color: 'var(--amber)',
                  },
                  {
                    label: 'Est. paid down',
                    value: (() => {
                      const now = debtSeries.reduce((sum, s) => {
                        const pt = s.points.find(p => p.date === today);
                        return sum + (pt?.value ?? 0) * 100;
                      }, 0);
                      const later = debtSeries.reduce((sum, s) => {
                        const pt = s.points[s.points.length - 1];
                        return sum + (pt?.value ?? 0) * 100;
                      }, 0);
                      return fmtCents(Math.round(now - later));
                    })(),
                    color: 'var(--green)',
                  },
                ]} />
                <div style={{ width: '100%', height: 240 }}>
                  <ResponsiveContainer>
                    {debtMode === 'lines' ? (
                      <LineChart data={debtChartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border2)" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={fmtTickDate}
                          minTickGap={40}
                          stroke="var(--text3)"
                          fontSize={11}
                        />
                        <YAxis
                          tickFormatter={fmtTickMoney}
                          stroke="var(--text3)"
                          fontSize={11}
                          width={52}
                        />
                        <Tooltip content={<MoneyTooltip showTotal />} />
                        <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
                        <ReferenceLine
                          x={today}
                          stroke="var(--text2)"
                          strokeDasharray="4 4"
                          label={{ value: 'today', position: 'top', fill: 'var(--text2)', fontSize: 10 }}
                        />
                        {debtSeries.map((s, i) => (
                          <Line
                            key={s.key}
                            type="monotone"
                            dataKey={s.key}
                            name={s.label}
                            stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                          />
                        ))}
                      </LineChart>
                    ) : (
                      <AreaChart data={debtChartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border2)" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={fmtTickDate}
                          minTickGap={40}
                          stroke="var(--text3)"
                          fontSize={11}
                        />
                        <YAxis
                          tickFormatter={fmtTickMoney}
                          stroke="var(--text3)"
                          fontSize={11}
                          width={52}
                        />
                        <Tooltip content={<MoneyTooltip showTotal />} />
                        <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
                        <ReferenceLine
                          x={today}
                          stroke="var(--text2)"
                          strokeDasharray="4 4"
                          label={{ value: 'today', position: 'top', fill: 'var(--text2)', fontSize: 10 }}
                        />
                        {debtSeries.map((s, i) => (
                          <Area
                            key={s.key}
                            type="monotone"
                            dataKey={s.key}
                            name={s.label}
                            stackId="1"
                            stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                            fill={SERIES_COLORS[i % SERIES_COLORS.length]}
                            fillOpacity={0.55}
                            isAnimationActive={false}
                          />
                        ))}
                      </AreaChart>
                    )}
                  </ResponsiveContainer>
                </div>
                <div className="muted tiny" style={{ marginTop: 8 }}>
                  Projection uses your recurring debt-payment rules where present,
                  and the minimum payment otherwise. Actual paydown will differ
                  with extra principal, balance transfers, or promo expirations.
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Checking balance chart builder
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Returns one point per day:
 *   { date, balance, projected: null }  — historical (up to today)
 *   { date, balance: null, projected }  — future
 * Today's point has both non-null so the two series connect seamlessly.
 *
 * Historical: walk backward from today's actual ledger sum.
 * Future: walk forward using forecast API rows (same source of truth as the
 *   Forecast screen). This correctly handles paid/skipped items, adjusted
 *   amounts, rescheduled dates, and overrides like the WF CC Club Tab amount.
 *   forecastRows = null means still loading → return history only (no future).
 */
function buildCheckingBalanceSeries(checkingId, todayBalanceCents, allTxns, forecastRows, accounts, startIso, endIso, today) {
  const acctKindMap = new Map((accounts ?? []).map(a => [a.id, a.kind]));
  const txnByDate = new Map();
  for (const t of allTxns) {
    if (t.account_id !== checkingId) continue;
    const cur = txnByDate.get(t.occurred_on) ?? 0;
    txnByDate.set(t.occurred_on, cur + t.amount_cents);
  }

  // ── Past ──
  const past = [];
  let bal = todayBalanceCents;
  for (let d = today; d >= startIso; d = addDaysIso(d, -1)) {
    past.push({ date: d, balance: bal / 100, projected: null });
    const delta = txnByDate.get(d) ?? 0;
    bal = bal - delta;
  }
  past.reverse();

  // Bridge: today gets both balance and projected so the two series connect
  if (past.length > 0) {
    past[past.length - 1].projected = past[past.length - 1].balance;
  }

  // Still loading forecast — return history only
  if (!forecastRows) return past;

  // ── Future: sum forecast rows by date, skipping paid/skipped ──
  // Paid rows already landed in actual transactions (history leg).
  // Skipped rows should not affect the projected balance.
  //
  // Not all forecast rows hit Checking — filter by the same logic used elsewhere:
  //   Two-legged types (Savings, Transfer, Debt Payment) always debit Checking
  //   Income landing in Checking credits it
  //   Other outflows: include if account kind is checking/credit/cash
  const TWO_LEGGED_TYPES = new Set(['Savings', 'Transfer', 'Debt Payment']);
  const CHECKING_FLOW_KINDS = new Set(['checking', 'credit', 'cash']);

  const futureEventsByDate = new Map();
  for (const row of forecastRows) {
    if (row.paid || row.skipped) continue;
    const d = row.date;
    if (d <= today || d > endIso) continue;

    const isTwoLegged = TWO_LEGGED_TYPES.has(row.type);
    const isIncome = row.type === 'Income';
    const isCheckingAcct = row.account_id === checkingId;

    let include = false;
    if (isTwoLegged) {
      include = true;
    } else if (isIncome) {
      include = isCheckingAcct;
    } else {
      include = isCheckingAcct || CHECKING_FLOW_KINDS.has(acctKindMap.get(row.account_id));
    }
    if (!include) continue;

    const cur = futureEventsByDate.get(d) ?? 0;
    futureEventsByDate.set(d, cur + row.amount_cents);
  }

  // ── Future: walk forward ──
  const future = [];
  let projBal = todayBalanceCents;
  for (let d = addDaysIso(today, 1); d <= endIso; d = addDaysIso(d, 1)) {
    const delta = futureEventsByDate.get(d) ?? 0;
    projBal += delta;
    future.push({ date: d, balance: null, projected: projBal / 100 });
  }

  return [...past, ...future];
}

// ═════════════════════════════════════════════════════════════════════════════
// Monthly income vs expense
// ═════════════════════════════════════════════════════════════════════════════

// ═════════════════════════════════════════════════════════════════════════════
// Cash flow: last month / this month / next month projected
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Returns exactly 3 columns: last month, current month, next month.
 *
 * Income normalization rule:
 *   Paychecks are scheduled on the 1st and 16th but may arrive up to 3 days
 *   early when those dates fall on a weekend/holiday. Any positive Checking
 *   transaction occurring on the 26th–31st of a month in the "Income" pattern
 *   (i.e. tagged as income and ~paycheck-sized) is attributed to the 1st of
 *   the NEXT month, not the calendar month it landed in.
 *
 *   More precisely: we use a 3-day look-ahead window. A transaction on day D
 *   of month M is "next-month income" if D >= 26 AND it looks like a paycheck
 *   (positive, not a transfer, amount >= $3,000).
 *
 * For last/current month: sums from actual transactions (transfers excluded).
 * For next month: sums from active recurring rules, same transfer exclusion.
 *   Two-legged rules (Savings, Transfer, Debt Payment) are excluded from the
 *   projected expenses bar (they're money movement, not spending).
 */
function buildCashFlowSummary(allTxns, checkingId, today, lookups, nextMonthForecast) {
  const [ty, tm] = today.split('-').map(Number);

  // Month boundaries
  const thisMonthStr  = `${ty}-${String(tm).padStart(2, '0')}`;
  const lastMonthDate = new Date(Date.UTC(ty, tm - 2, 1));
  const lastMonthStr  = `${lastMonthDate.getUTCFullYear()}-${String(lastMonthDate.getUTCMonth() + 1).padStart(2, '0')}`;
  const nextMonthDate = new Date(Date.UTC(ty, tm, 1));
  const nextMonthStr  = `${nextMonthDate.getUTCFullYear()}-${String(nextMonthDate.getUTCMonth() + 1).padStart(2, '0')}`;

  // Normalize income: paycheck (>==$3k, positive, no transfer) arriving day 26+
  // is attributed to the 1st of the following month.
  const normalizeMonth = (iso, amtCents) => {
    if (amtCents <= 0) return iso.slice(0, 7);
    const day = parseInt(iso.slice(8, 10), 10);
    if (day >= 26 && amtCents >= 300000) {
      const d = new Date(Date.UTC(parseInt(iso.slice(0, 4)), parseInt(iso.slice(5, 7), 10) - 1, 1));
      d.setUTCMonth(d.getUTCMonth() + 1);
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    }
    return iso.slice(0, 7);
  };

  const buckets = {
    [lastMonthStr]: { income: 0, expense: 0, savings: 0 },
    [thisMonthStr]: { income: 0, expense: 0, savings: 0 },
    [nextMonthStr]: { income: 0, expense: 0, savings: 0 },
  };

  for (const t of allTxns) {
    if (t.account_id !== checkingId) continue;

    if (t.transfer_pair_id) {
      // Two-legged transactions: split by category
      //   "Debt Payment" category → treat as expense (you still owe it)
      //   "Savings - *" category → treat as savings (money movement, but earmarked)
      // Only count the debit leg (negative amount_cents on Checking)
      if (t.amount_cents >= 0) continue;
      const month = t.occurred_on.slice(0, 7);
      if (!buckets[month]) continue;
      const cat = lookups?.catName(t.category_id) ?? '';
      if (cat === 'Debt Payment') {
        buckets[month].expense += Math.abs(t.amount_cents);
      } else {
        // Savings - Emergency Fund, Savings - Golf Fund, etc.
        buckets[month].savings += Math.abs(t.amount_cents);
      }
      continue;
    }

    // Non-transfer transactions
    const effectiveMonth = normalizeMonth(t.occurred_on, t.amount_cents);
    if (!buckets[effectiveMonth]) continue;
    if (t.amount_cents > 0) {
      buckets[effectiveMonth].income += t.amount_cents;
    } else {
      buckets[effectiveMonth].expense += Math.abs(t.amount_cents);
    }
  }

  // Next month: sum from forecast API rows (same engine as Forecast screen).
  // Avoids drift from our own expandRule logic — correct discretionary count,
  // actual WF CC override amounts, EveryNWeeks cadence, etc.
  if (nextMonthForecast?.rows) {
    for (const r of nextMonthForecast.rows) {
      const t = r.type;
      const amt = r.amount_cents;
      if (t === 'Income') {
        buckets[nextMonthStr].income += amt;
      } else if (t === 'Savings' || t === 'Transfer') {
        buckets[nextMonthStr].savings += Math.abs(amt);
      } else {
        // Bill, Debt Payment, Discretionary, Sinking Drain — all expenses
        buckets[nextMonthStr].expense += Math.abs(amt);
      }
    }
  }

  const fmtMonthLabel = (ym) => {
    const [y, m] = ym.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, 1))
      .toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', year: '2-digit' });
  };

  const col = (key, monthStr, sublabel, isProjected) => {
    const b = buckets[monthStr];
    return {
      key, label: fmtMonthLabel(monthStr), sublabel, isProjected,
      income:     b.income   / 100,
      expense:    b.expense  / 100,
      savings:    b.savings  / 100,
      // Net liquid = income minus expenses only (savings still accessible)
      netLiquid: (b.income - b.expense) / 100,
      // Net true = income minus everything leaving your hands
      net:       (b.income - b.expense - b.savings) / 100,
    };
  };

  return [
    col('last',    lastMonthStr, 'Last month',             false),
    col('current', thisMonthStr, 'This month',             false),
    col('next',    nextMonthStr, 'Next month (projected)', true),
  ];
}

// ═════════════════════════════════════════════════════════════════════════════
// Savings & Debt engines (unchanged from original)
// ═════════════════════════════════════════════════════════════════════════════

function buildSavingsSeries(state, allTxns, startIso, endIso, today) {
  const linkedGoals = state.goals.filter(g => !g.archived && g.account_id);
  if (linkedGoals.length === 0) return [];

  const txnsByAcct = groupBy(allTxns, t => t.account_id);
  const ruleEvents = expandRulesAcrossWindow(state.recurring, today, endIso);

  return linkedGoals.map(goal => {
    const acctTxns = txnsByAcct.get(goal.account_id) ?? [];
    const acctRules = ruleEvents.filter(e => e.account_id === goal.account_id);
    const liveBalance = goal.live_balance_cents ?? goal.current_cents ?? 0;
    const points = projectAccountBalance({
      todayBalance: liveBalance,
      transactions: acctTxns,
      futureEvents: acctRules,
      startIso,
      endIso,
      today,
    });
    return { key: `goal_${goal.id}`, label: goal.name, points };
  });
}

function buildDebtSeries(state, allTxns, startIso, endIso, today) {
  const linkedDebts = state.debts.filter(d => !d.archived && d.account_id);
  if (linkedDebts.length === 0) return [];

  const txnsByAcct = groupBy(allTxns, t => t.account_id);

  const debtPayRuleEvents = [];
  for (const rule of state.recurring) {
    if (!rule.active || rule.archived) continue;
    if (rule.type !== 'Debt Payment') continue;
    const dates = expandRule(rule, today, endIso);
    for (const date of dates) {
      debtPayRuleEvents.push({
        date,
        signed_for_balance: -Math.abs(rule.amount_cents),
        account_id: rule.account_id,
      });
    }
  }

  return linkedDebts.map(debt => {
    const acctTxns = txnsByAcct.get(debt.account_id) ?? [];
    const liveBalance = debt.live_balance_cents ?? debt.current_cents ?? 0;
    const ruleEvents = debtPayRuleEvents.filter(e => e.account_id === debt.account_id);
    const futureEvents = ruleEvents.length > 0
      ? ruleEvents
      : projectDebtMinPayments(debt, today, endIso);
    const points = projectAccountBalance({
      todayBalance: liveBalance,
      transactions: acctTxns,
      futureEvents,
      startIso,
      endIso,
      today,
      isDebt: true,
      clampZero: true,
    });
    return { key: `debt_${debt.id}`, label: debt.name, points };
  });
}

function projectAccountBalance({ todayBalance, transactions, futureEvents, startIso, endIso, today, isDebt = false, clampZero = false }) {
  const txnByDate = new Map();
  for (const t of transactions) {
    const cur = txnByDate.get(t.occurred_on) ?? 0;
    txnByDate.set(t.occurred_on, cur + t.amount_cents);
  }

  const past = [];
  let bal = todayBalance;
  for (let d = today; d >= startIso; d = addDaysIso(d, -1)) {
    past.push({ date: d, value: bal / 100 });
    const todaysDelta = txnByDate.get(d) ?? 0;
    if (isDebt) {
      bal = Math.max(0, bal + todaysDelta);
    } else {
      bal = bal - todaysDelta;
    }
  }
  past.reverse();

  const eventByDate = new Map();
  for (const e of futureEvents) {
    if (e.date <= today || e.date > endIso) continue;
    const cur = eventByDate.get(e.date) ?? 0;
    eventByDate.set(e.date, cur + e.signed_for_balance);
  }

  const future = [];
  bal = todayBalance;
  for (let d = addDaysIso(today, 1); d <= endIso; d = addDaysIso(d, 1)) {
    const delta = eventByDate.get(d) ?? 0;
    bal = bal + delta;
    if (clampZero) bal = Math.max(0, bal);
    future.push({ date: d, value: bal / 100 });
  }

  return [...past, ...future];
}

function expandRulesAcrossWindow(rules, today, endIso) {
  const events = [];
  for (const rule of rules) {
    if (!rule.active || rule.archived) continue;
    if (rule.type === 'Expense') continue;
    if (rule.type === 'Debt Payment') continue;
    const dates = expandRule(rule, today, endIso);
    for (const date of dates) {
      events.push({
        date,
        amount_cents: rule.amount_cents,
        signed_for_balance: rule.amount_cents,
        account_id: rule.account_id,
      });
    }
  }
  return events;
}

function expandRule(rule, fromIso, toIso) {
  const dates = [];
  const start = rule.starts_on || fromIso;
  const end   = rule.ends_on || toIso;
  const windowStart = start > fromIso ? start : fromIso;
  const windowEnd   = end < toIso ? end : toIso;
  if (windowStart > windowEnd) return dates;

  const monthsActive = parseMonthsActive(rule.months_active);
  const inMonths = (iso) => monthsActive == null || monthsActive.has(parseInt(iso.slice(5, 7), 10));

  switch (rule.frequency) {
    case 'Monthly':
    case 'EveryNMonths': {
      const anchorDay = parseInt(rule.anchor || '1', 10);
      const stride = rule.every_n || 1;
      let cursor = startOfMonthIso(windowStart);
      while (cursor <= windowEnd) {
        const iso = clampDayInMonth(cursor, anchorDay);
        if (iso >= windowStart && iso <= windowEnd && inMonths(iso)) dates.push(iso);
        cursor = addMonthsIso(cursor, stride);
      }
      break;
    }
    case 'Quarterly': {
      const anchorDay = parseInt(rule.anchor || '1', 10);
      let cursor = startOfMonthIso(windowStart);
      while (cursor <= windowEnd) {
        const iso = clampDayInMonth(cursor, anchorDay);
        if (iso >= windowStart && iso <= windowEnd && inMonths(iso)) dates.push(iso);
        cursor = addMonthsIso(cursor, 3);
      }
      break;
    }
    case 'Annual': {
      const monthDay = rule.anchor && rule.anchor.includes('-')
        ? rule.anchor
        : `01-${String(parseInt(rule.anchor || '1', 10)).padStart(2, '0')}`;
      const startYear = parseInt(windowStart.slice(0, 4), 10);
      const endYear = parseInt(windowEnd.slice(0, 4), 10);
      for (let y = startYear; y <= endYear; y++) {
        const iso = `${y}-${monthDay}`;
        if (iso >= windowStart && iso <= windowEnd && inMonths(iso)) dates.push(iso);
      }
      break;
    }
    case 'Weekly': {
      const anchorDow = dowFromAnchor(rule.anchor);
      let cursor = windowStart;
      while (dowOfIso(cursor) !== anchorDow) {
        cursor = addDaysIso(cursor, 1);
        if (cursor > windowEnd) break;
      }
      while (cursor <= windowEnd) {
        if (inMonths(cursor)) dates.push(cursor);
        cursor = addDaysIso(cursor, 7);
      }
      break;
    }
    case 'BiWeekly':
    case 'EveryNWeeks': {
      const everyN = rule.frequency === 'BiWeekly' ? 2 : (rule.every_n || 1);
      const anchorDow = dowFromAnchor(rule.anchor);
      let cursor = windowStart;
      while (dowOfIso(cursor) !== anchorDow) {
        cursor = addDaysIso(cursor, 1);
        if (cursor > windowEnd) break;
      }
      while (cursor <= windowEnd) {
        if (inMonths(cursor)) dates.push(cursor);
        cursor = addDaysIso(cursor, 7 * everyN);
      }
      break;
    }
    case 'PairedWithPaycheck': {
      let cursor = startOfMonthIso(windowStart);
      while (cursor <= windowEnd) {
        for (const day of [1, 16]) {
          const iso = clampDayInMonth(cursor, day);
          if (iso >= windowStart && iso <= windowEnd && inMonths(iso)) dates.push(iso);
        }
        cursor = addMonthsIso(cursor, 1);
      }
      break;
    }
    case 'OneTime': {
      const iso = rule.starts_on;
      if (iso && iso >= windowStart && iso <= windowEnd) dates.push(iso);
      break;
    }
  }
  return dates;
}

function projectDebtMinPayments(debt, today, endIso) {
  if (!debt.min_payment_cents || debt.min_payment_cents <= 0) return [];
  const events = [];
  const dueDay = debt.due_day || 1;
  let cursor = startOfMonthIso(today);
  while (cursor <= endIso) {
    const iso = clampDayInMonth(cursor, dueDay);
    if (iso > today && iso <= endIso) {
      events.push({ date: iso, amount_cents: debt.min_payment_cents, signed_for_balance: -debt.min_payment_cents });
    }
    cursor = addMonthsIso(cursor, 1);
  }
  return events;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function zipSeries(seriesList) {
  if (seriesList.length === 0) return [];
  const dates = seriesList[0].points.map(p => p.date);
  return dates.map((date, i) => {
    const row = { date };
    for (const s of seriesList) {
      const pt = s.points[i];
      row[s.key] = pt ? pt.value : null;
    }
    return row;
  });
}

function groupBy(arr, keyFn) {
  const m = new Map();
  for (const x of arr) {
    const k = keyFn(x);
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(x);
  }
  return m;
}

function parseMonthsActive(s) {
  if (!s || s === 'All' || s === '') return null;
  const months = new Set();
  const NAMES = { jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12 };
  for (const part of s.split(',').map(p => p.trim().toLowerCase())) {
    if (NAMES[part]) months.add(NAMES[part]);
    else if (/^\d+$/.test(part)) months.add(parseInt(part, 10));
  }
  return months.size > 0 ? months : null;
}

function dowFromAnchor(anchor) {
  if (anchor == null) return 5;
  if (/^\d+$/.test(String(anchor))) {
    const n = parseInt(anchor, 10);
    return Math.max(0, Math.min(6, n));
  }
  const NAMES = { sun:0,mon:1,tue:2,wed:3,thu:4,fri:5,sat:6 };
  const k = String(anchor).slice(0, 3).toLowerCase();
  return NAMES[k] ?? 5;
}

function addMonthsIso(iso, months) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCMonth(dt.getUTCMonth() + months);
  return dt.toISOString().slice(0, 10);
}

function startOfMonthIso(iso) {
  return iso.slice(0, 8) + '01';
}

function clampDayInMonth(monthStartIso, day) {
  const [y, m] = monthStartIso.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const clamped = Math.min(Math.max(1, day), lastDay);
  return `${monthStartIso.slice(0, 8)}${String(clamped).padStart(2, '0')}`;
}

function dowOfIso(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

function fmtTickDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', year: '2-digit' });
}

function fmtTickMoney(v) {
  if (v == null) return '';
  if (Math.abs(v) >= 1000) return `$${Math.round(v / 1000)}k`;
  return `$${Math.round(v)}`;
}
